/**
 * What theme should be on screen, decided without React in the room.
 *
 * Jason, 2026-08-30: "If I set my Mac OS to dark mode, it knows and changes to
 * dark mode also?" — it does now, and the interesting part is not the media
 * query, it is the three-way choice underneath it. A stored "dark" and a
 * system that happens to be dark look identical on screen and are not the same
 * thing: one survives the Mac changing its mind, the other is the Mac.
 *
 * This lives apart from theme-context.tsx because the provider needs a DOM and
 * a renderer, and these two functions need neither — which is what makes the
 * rules below assertable.
 */

/** What the person chose. "system" is a live subscription, not a snapshot. */
export type ThemePreference = "light" | "dark" | "system"

/** What is actually on screen. Never "system" — that has been resolved. */
export type Theme = "light" | "dark"

export const THEME_STORAGE_KEY = "masteros-theme"
export const DARK_QUERY = "(prefers-color-scheme: dark)"

/**
 * The window chrome, per theme — Safari's installed app paints its title bar
 * with <meta name="theme-color">, so this has to move when the theme does.
 *
 * These are the two `--background` values from globals.css converted out of
 * oklch: `oklch(1 0 0)` and `oklch(0.145 0 0)`. Hard-coded because a meta tag
 * takes a colour, not a CSS variable; src/app/manifest.test.ts is the thread
 * that keeps them tied to the stylesheet.
 */
export const CHROME: Record<Theme, string> = { light: "#ffffff", dark: "#0a0a0a" }

/**
 * DEFAULTS TO "system", including for anything unrecognised.
 *
 * Everyone who already picked keeps their pick: the stored value is still
 * "light" or "dark", and those still mean pinned. Only someone who never
 * touched the toggle starts following their Mac — the one group for whom this
 * cannot change anything they had deliberately set.
 */
export function parsePreference(saved: string | null | undefined): ThemePreference {
  return saved === "light" || saved === "dark" || saved === "system" ? saved : "system"
}

/** Collapse a choice plus the OS's current state into what to actually render. */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): Theme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light"
  return preference
}
